/**
 * EventChip — calendar event block.
 *
 * Two modes:
 *   - **Inline** (default): fills its parent. Use inside `<CalendarCell>` for
 *     month / agenda layouts.
 *   - **Positioned**: absolute, slotted into a relative-positioned swimlane
 *     row. Provide `start` / `span` / `totalSlots` to compute its position.
 *     Use inside the `<Calendar.SlotRow>` swimlane for technician schedules.
 *
 *   <EventChip tone="accent" title="Náhradní vozidlo" />
 *   <EventChip tone="warn" title="Stezka" sub="…" start={4} span={8} totalSlots={20} />
 *
 * Tone vocabulary is the four locked semantic palettes (accent / warn / ok /
 * ink / neutral). Module-specific palettes (e.g. plum 'PDI', moss 'service')
 * are deferred to module phases via className overrides.
 *
 * Source: .agents/design/project/artboard-radical-plan.jsx:153-181
 */

import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export type EventChipTone = 'accent' | 'warn' | 'ok' | 'ink' | 'neutral' | 'danger';

const TONE_CLASSES: Record<EventChipTone, string> = {
  accent: 'bg-accent-soft text-accent-text border-l-accent',
  warn: 'bg-warn-soft text-warn border-l-warn',
  ok: 'bg-ok-soft text-ok border-l-ok',
  ink: 'bg-surface-2 text-ink border-l-ink',
  neutral: 'bg-surface-2 text-ink-2 border-l-ink-3',
  danger: 'bg-red-soft text-red-text border-l-red',
};

/**
 * A chip whose time is over keeps its tone but is faded: the tint and the edge are lighter while the TEXT keeps full strength (fading the
 * whole chip with `opacity` would drop the label below AA). The class names are written out in full so Tailwind finds them.
 */
const TONE_CLASSES_FADED: Record<EventChipTone, string> = {
  accent: 'bg-accent-soft/50 text-accent-text border-l-accent/50',
  warn: 'bg-warn-soft/50 text-warn border-l-warn/50',
  ok: 'bg-ok-soft/50 text-ok border-l-ok/50',
  ink: 'bg-surface-2/50 text-ink border-l-ink/50',
  neutral: 'bg-surface-2/50 text-ink-2 border-l-ink-3/50',
  danger: 'bg-red-soft/50 text-red-text border-l-red/50',
};

/**
 * Confirmation state of a service event — a leading icon in the title, plus a
 * dashed outline while it still awaits confirmation. The chip's fill stays the
 * event-type tone (a no-show is switched to `danger` by the caller).
 */
export type EventChipStatus = 'probehlo' | 'neprobehlo' | 'k_potvrzeni';

const STATUS_ICON: Record<EventChipStatus, { icon: IconName; label: string }> = {
  probehlo: { icon: 'check', label: 'Proběhlo' },
  neprobehlo: { icon: 'close', label: 'Neproběhlo' },
  k_potvrzeni: { icon: 'clock', label: 'K potvrzení' },
};

interface EventChipProps {
  /** Bold first line. */
  title: ReactNode;
  /** Optional dimmer second line — clamped to 2 visual lines when inline. */
  sub?: ReactNode;
  tone?: EventChipTone;
  /** Its time is over: the same tone, with a lighter tint and edge (the text keeps its strength). */
  faded?: boolean;
  /** Confirmation state (servis): icon before the title, dashed outline while pending. */
  status?: EventChipStatus | null;
  /** A leading icon naming what the chip IS (before the confirmation icon), e.g. the wrench of a workshop booking drawn among rentals. */
  icon?: IconName;
  /** A dashed outline in the chip's own tone: a booking of another kind drawn in the same row (it is not a rental of the car). */
  dashed?: boolean;
  /** Slot index where the chip starts (0-indexed). Required for positioned mode. */
  start?: number;
  /** Number of slots the chip spans. Required for positioned mode. */
  span?: number;
  /** Total slots in the row. Required for positioned mode. */
  totalSlots?: number;
  onClick?: () => void;
  /** Native title attribute (tooltip). Defaults to `${title} — ${sub}`. */
  hoverTitle?: string;
  /**
   * When set, the subtitle auto-sizes: if it fits the chip width on a single
   * line it renders one larger line; otherwise it falls back to the compact
   * two-line clamp. Off by default so other layouts keep the dense chip.
   */
  autoSizeSub?: boolean;
  /**
   * Optional CSS color for a small author/owner dot in the top-right corner.
   * The chip's fill/border still encode the event *type*; this dot is a
   * secondary signal for *who* owns the event. Hidden when omitted.
   */
  accentDot?: string;
  className?: string;
  style?: CSSProperties;
  /** `data-testid` of the chip (e2e). */
  testId?: string;
}

export function EventChip({
  title,
  sub,
  tone = 'neutral',
  faded = false,
  status = null,
  icon,
  dashed = false,
  start,
  span,
  totalSlots,
  onClick,
  hoverTitle,
  autoSizeSub = false,
  accentDot,
  className = '',
  style,
  testId,
}: EventChipProps) {
  const subRef = useRef<HTMLSpanElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const [subOneLine, setSubOneLine] = useState(false);

  // Pick the subtitle size by measuring an off-screen probe rendered at the
  // larger single-line size: if its natural width fits the chip's text column
  // we promote the subtitle to one big line, otherwise keep the two-line clamp.
  useLayoutEffect(() => {
    if (!autoSizeSub || !sub) return;
    const subEl = subRef.current;
    const probeEl = probeRef.current;
    if (!subEl || !probeEl) return;
    const measure = () => {
      setSubOneLine(probeEl.scrollWidth <= subEl.clientWidth + 0.5);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(subEl);
    return () => ro.disconnect();
  }, [autoSizeSub, sub]);

  const positioned =
    typeof start === 'number' &&
    typeof span === 'number' &&
    typeof totalSlots === 'number' &&
    totalSlots > 0;

  const positionStyle: CSSProperties | undefined = positioned
    ? {
        left: `calc(${start} * (100% / ${totalSlots}) + 2px)`,
        width: `calc(${span} * (100% / ${totalSlots}) - 4px)`,
      }
    : undefined;

  const computedTitle =
    hoverTitle ??
    (typeof title === 'string'
      ? title + (typeof sub === 'string' ? ' — ' + sub : '')
      : undefined);

  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={computedTitle}
      data-testid={testId}
      style={{ ...positionStyle, ...style }}
      className={
        'flex flex-col gap-px overflow-hidden border-l-[3px] px-2 py-[5px] text-left text-[11px] leading-[1.25] ' +
        (faded ? TONE_CLASSES_FADED[tone] : TONE_CLASSES[tone]) +
        ' ' +
        (status === 'k_potvrzeni'
          ? 'outline-dashed outline-1 -outline-offset-1 outline-warn '
          : dashed
            ? 'outline-dashed outline-1 -outline-offset-1 outline-accent '
            : '') +
        (positioned
          ? 'absolute inset-y-1 transition-transform duration-100 hover:z-10 hover:translate-x-0.5 hover:shadow-[-2px_4px_12px_rgba(0,0,0,0.12)] '
          : 'relative w-full ') +
        (onClick
          ? 'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent '
          : '') +
        className
      }
    >
      <span
        className={
          'truncate font-semibold tracking-[-0.01em] ' +
          (autoSizeSub ? 'text-[13.5px]' : 'text-[11.5px]') +
          (accentDot ? ' pr-4' : '')
        }
      >
        {icon && <Icon name={icon} size={autoSizeSub ? 13 : 11} stroke={2} className="mr-1 inline-block align-[-1px]" />}
        {status && (
          <Icon
            name={STATUS_ICON[status].icon}
            size={autoSizeSub ? 13 : 11}
            stroke={2}
            title={STATUS_ICON[status].label}
            className="mr-1 inline-block align-[-1px]"
          />
        )}
        {title}
      </span>
      {sub && (
        <>
          <span
            ref={subRef}
            className={
              'text-ink-2 ' +
              (autoSizeSub
                ? subOneLine
                  ? 'truncate text-[13px] leading-snug'
                  : 'line-clamp-2 text-[12px] leading-snug'
                : 'line-clamp-2 text-[10.5px] leading-tight')
            }
          >
            {sub}
          </span>
          {autoSizeSub && (
            <span
              ref={probeRef}
              aria-hidden
              className="pointer-events-none absolute -left-[9999px] top-0 whitespace-nowrap text-[13px] leading-snug"
            >
              {sub}
            </span>
          )}
        </>
      )}
      {accentDot && (
        <span
          aria-hidden
          className="absolute right-1.5 top-1.5 size-3 rounded-full ring-2 ring-surface"
          style={{ backgroundColor: accentDot }}
        />
      )}
    </Tag>
  );
}
